
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ChippieConversation, ChippieMessage, CreateChippieConversationInput, CreateChippieMessageInput } from '@/types/database.types';
import { useAuth } from '@/contexts/AuthContext';

// Get all conversations for the current user
export const useGetChippieConversations = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['chippie-conversations'],
    queryFn: async (): Promise<ChippieConversation[]> => {
      if (!user) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('chippie_conversations')
        .select('*')
        .order('last_message_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(conversation => ({
        ...conversation,
        key_topics: conversation.key_topics || [],
        conversation_context: conversation.conversation_context as ChippieConversation['conversation_context']
      }));
    },
    enabled: !!user,
  });
};

// Get messages for a specific conversation
export const useGetChippieMessages = (conversationId: string | null) => {
  return useQuery({
    queryKey: ['chippie-messages', conversationId],
    queryFn: async (): Promise<ChippieMessage[]> => {
      if (!conversationId) return [];
      
      const { data, error } = await supabase
        .from('chippie_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      // Type assertion to ensure role and metadata are properly typed
      return (data || []).map(message => ({
        ...message,
        role: message.role as 'user' | 'assistant',
        metadata: message.metadata as ChippieMessage['metadata']
      }));
    },
    enabled: !!conversationId,
  });
};

// Create a new conversation
export const useCreateChippieConversation = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: CreateChippieConversationInput): Promise<ChippieConversation> => {
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('chippie_conversations')
        .insert({
          user_id: user.id,
          title: input.title,
        })
        .select()
        .single();

      if (error) throw error;
      return {
        ...data,
        key_topics: data.key_topics || [],
        conversation_context: data.conversation_context as ChippieConversation['conversation_context']
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chippie-conversations'] });
    },
  });
};

// Create a new message
export const useCreateChippieMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateChippieMessageInput): Promise<ChippieMessage> => {
      const { data, error } = await supabase
        .from('chippie_messages')
        .insert({
          conversation_id: input.conversation_id,
          role: input.role,
          content: input.content,
          metadata: input.metadata,
        })
        .select()
        .single();

      if (error) throw error;
      
      // Type assertion to ensure role and metadata are properly typed
      return {
        ...data,
        role: data.role as 'user' | 'assistant',
        metadata: data.metadata as ChippieMessage['metadata']
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['chippie-messages', data.conversation_id] });
      queryClient.invalidateQueries({ queryKey: ['chippie-conversations'] });
    },
  });
};

// Update conversation title
export const useUpdateChippieConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }): Promise<ChippieConversation> => {
      const { data, error } = await supabase
        .from('chippie_conversations')
        .update({ title })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return {
        ...data,
        key_topics: data.key_topics || [],
        conversation_context: data.conversation_context as ChippieConversation['conversation_context']
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chippie-conversations'] });
    },
  });
};

// Delete a conversation
export const useDeleteChippieConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase
        .from('chippie_conversations')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chippie-conversations'] });
    },
  });
};
